import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, PieChart, Pie, Cell
} from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const CRIMSON = '#8B1A1A';
const BORDER = '#EAE6E1';
const MUTED = '#888';

// ── Formatters ───────────────────────────────────────────────────────────────
const fmtNumber = (n) => n == null ? '—' : new Intl.NumberFormat('en-GB').format(n);
const fmtPct = (n) => n == null ? '—' : `${n}%`;
const fmtHours = (h) => h == null ? '—' : `${h}h`;
const fmtScore = (s) => s == null ? '—' : `${s}/100`;
const fmtCredits = (n) => n == null ? '—' : `${fmtNumber(n)} cr`;

// Delta from previous-period value. Positive = green for sends/replies, red for bounces.
function computeDelta(current, previous, { inverse = false } = {}) {
  if (previous == null || previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  const direction = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  const positive = inverse ? direction === 'down' : direction === 'up';
  const negative = inverse ? direction === 'up' : direction === 'down';
  return { pct: Math.round(pct * 10) / 10, direction, positive, negative };
}

function DeltaPill({ delta }) {
  if (!delta || delta.direction === 'flat') return null;
  const colour = delta.positive ? '#2F855A' : delta.negative ? '#B91C1C' : MUTED;
  const arrow = delta.direction === 'up' ? '↑' : '↓';
  const sign = delta.pct > 0 ? '+' : '';
  return (
    <span style={{ fontSize: 12, color: colour, fontWeight: 500 }}>
      {arrow} {sign}{delta.pct}%
    </span>
  );
}

// Tinted palette: pastel background + strong text per metric category.
const PALETTE = {
  volume:    { bg: '#EFF6FF', text: '#1E40AF', label: '#3B82F6' },
  engage:    { bg: '#ECFDF5', text: '#065F46', label: '#10B981' },
  risk:      { bg: '#FFF1F2', text: '#9F1239', label: '#F43F5E' },
  score:     { bg: '#F5F3FF', text: '#5B21B6', label: '#8B5CF6' },
  time:      { bg: '#ECFEFF', text: '#155E75', label: '#06B6D4' },
  infra:     { bg: '#FFFBEB', text: '#92400E', label: '#F59E0B' },
  cost:      { bg: '#FDF2F8', text: '#9D174D', label: '#EC4899' },
};

function Tile({ label, value, delta, hint, category = 'volume' }) {
  const c = PALETTE[category];
  return (
    <div style={{ background: c.bg, borderRadius: 10, padding: 16, minHeight: 110 }}>
      <div style={{ fontSize: 11, color: c.label, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: c.text, marginBottom: 4 }}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <DeltaPill delta={delta} />
        {hint && <span style={{ fontSize: 12, color: c.text, opacity: 0.7 }}>{hint}</span>}
      </div>
    </div>
  );
}

function ScoreTile({ label, value, score }) {
  const c = PALETTE.score;
  // Score bar colour adapts: green if healthy, amber medium, red low
  let bar = '#10B981';
  if (score == null) bar = '#94A3B8';
  else if (score < 60) bar = '#F43F5E';
  else if (score < 80) bar = '#F59E0B';
  return (
    <div style={{ background: c.bg, borderRadius: 10, padding: 16, minHeight: 110 }}>
      <div style={{ fontSize: 11, color: c.label, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: c.text, marginBottom: 4 }}>{value}</div>
      <div style={{ height: 5, background: 'rgba(139,92,246,0.15)', borderRadius: 3, overflow: 'hidden', marginTop: 10 }}>
        <div style={{ height: '100%', width: `${score || 0}%`, background: bar, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

export default function EmailMetrics() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [timeseries, setTimeseries] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [pacing, setPacing] = useState([]);
  const [utilisation, setUtilisation] = useState([]);
  const [suppressionsGrowth, setSuppressionsGrowth] = useState([]);
  const [mailboxesTable, setMailboxesTable] = useState([]);
  const [campaignsTable, setCampaignsTable] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [cohortDimension, setCohortDimension] = useState('sector');
  const [cohorts, setCohorts] = useState(null);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [domainHealth, setDomainHealth] = useState(null);
  const [domainHealthLoading, setDomainHealthLoading] = useState(false);
  const [replyIntent, setReplyIntent] = useState(null);
  const [replyIntentLoading, setReplyIntentLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const base = isGlobalMode ? `${API_BASE}/admin/metrics` : `${API_BASE}/clients/${clientId}/metrics`;
      const [ov, ts, fn, pc, ut, sg, mb, cmp, an] = await Promise.all([
        fetch(`${base}/overview`, { headers }),
        fetch(`${base}/timeseries?days=30`, { headers }),
        fetch(`${base}/funnel`, { headers }),
        fetch(`${base}/pacing`, { headers }),
        fetch(`${base}/utilisation`, { headers }),
        fetch(`${base}/suppressions-growth?days=30`, { headers }),
        fetch(`${base}/mailboxes`, { headers }),
        fetch(`${base}/campaigns`, { headers }),
        fetch(`${base}/anomalies`, { headers }),
      ]);
      if (!ov.ok) throw new Error(`Overview failed (${ov.status})`);
      setOverview(await ov.json());
      if (ts.ok) setTimeseries(await ts.json());
      if (fn.ok) setFunnel(await fn.json());
      if (pc.ok) setPacing(await pc.json());
      if (ut.ok) setUtilisation(await ut.json());
      if (sg.ok) setSuppressionsGrowth(await sg.json());
      if (mb.ok) setMailboxesTable(await mb.json());
      if (cmp.ok) setCampaignsTable(await cmp.json());
      if (an.ok) setAnomalies(await an.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, isGlobalMode, getToken]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Refetch cohorts whenever dimension changes (separate from main load)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCohortLoading(true);
      try {
        const token = await getToken();
        const base = isGlobalMode ? `${API_BASE}/admin/metrics` : `${API_BASE}/clients/${clientId}/metrics`;
        const r = await fetch(`${base}/cohorts?dimension=${cohortDimension}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok && !cancelled) setCohorts(await r.json());
      } catch (_) {}
      finally { if (!cancelled) setCohortLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [cohortDimension, clientId, isGlobalMode, getToken]);

  // Domain health: fetched once per page load; refresh button forces re-check
  const fetchDomainHealth = useCallback(async () => {
    setDomainHealthLoading(true);
    try {
      const token = await getToken();
      const base = isGlobalMode ? `${API_BASE}/admin/metrics` : `${API_BASE}/clients/${clientId}/metrics`;
      const r = await fetch(`${base}/domain-health`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setDomainHealth(await r.json());
    } catch (_) {}
    finally { setDomainHealthLoading(false); }
  }, [clientId, isGlobalMode, getToken]);

  useEffect(() => { fetchDomainHealth(); }, [fetchDomainHealth]);

  // Reply intent fetch
  const fetchReplyIntent = useCallback(async () => {
    setReplyIntentLoading(true);
    try {
      const token = await getToken();
      const base = isGlobalMode ? `${API_BASE}/admin/metrics` : `${API_BASE}/clients/${clientId}/metrics`;
      const r = await fetch(`${base}/reply-intent`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setReplyIntent(await r.json());
    } catch (_) {}
    finally { setReplyIntentLoading(false); }
  }, [clientId, isGlobalMode, getToken]);

  useEffect(() => { fetchReplyIntent(); }, [fetchReplyIntent]);

  if (loading && !overview) {
    return <div style={{ padding: 32, color: MUTED }}>Loading metrics…</div>;
  }
  if (error) {
    return <div style={{ padding: 32, color: '#B91C1C' }}>Could not load metrics: {error}</div>;
  }
  if (!overview) return null;

  const replyDelta = computeDelta(overview.reply_rate_30d, overview.prev_reply_rate_30d);
  const sentDelta = computeDelta(overview.sent_30d, overview.sent_prev_30d);
  const repliesDelta = computeDelta(overview.replies_30d, overview.replies_prev_30d);

  return (
    <div style={{ padding: '0 0 32px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>Metrics</div>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>
            {isGlobalMode ? 'Platform-wide overview' : 'Workspace overview'} · last 30 days vs previous 30 days
          </div>
        </div>
        <button onClick={fetchAll} style={{ background: 'white', border: `1px solid ${BORDER}`, padding: '8px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
          Refresh
        </button>
      </div>

      {/* ANOMALY BANNER (visible when anomalies detected) */}
      {anomalies && anomalies.length > 0 && (
        <AnomalyBanner anomalies={anomalies} />
      )}

      {/* LAYER 1 — KPI strip: 4 columns × 3 rows */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <Tile category="volume" label="Sent today" value={fmtNumber(overview.sent_today)} hint={`${fmtNumber(overview.sent_7d)} this week`} />
        <Tile category="volume" label="Sent (30 days)" value={fmtNumber(overview.sent_30d)} delta={sentDelta} />
        <Tile category="engage" label="Replies (30 days)" value={fmtNumber(overview.replies_30d)} delta={repliesDelta} />
        <Tile category="engage" label="Reply rate (30d)" value={fmtPct(overview.reply_rate_30d)} delta={replyDelta} />

        <Tile category="risk" label="Bounce rate (30d)" value={fmtPct(overview.bounce_rate_30d)} hint={`${fmtNumber(overview.bounces_30d)} bounces`} />
        <Tile category="risk" label="Complaint rate (30d)" value={fmtPct(overview.complaint_rate_30d)} hint={`${fmtNumber(overview.complaints_30d)} complaints`} />
        <ScoreTile label="Deliverability score" value={fmtScore(overview.deliverability_score)} score={overview.deliverability_score} />
        <Tile category="time" label="Time to reply (median)" value={fmtHours(overview.time_to_reply_median_hours)} hint="From send to first reply" />

        <Tile category="time" label="Conversation depth" value={overview.conversation_depth_avg || '—'} hint="Avg replies per contact" />
        <Tile category="infra" label="Active mailboxes" value={fmtNumber(overview.active_mailboxes)} hint={`${fmtNumber(overview.error_mailboxes)} error · ${fmtNumber(overview.mailboxes_warmed)} warming`} />
        <Tile category="infra" label="Active campaigns" value={fmtNumber(overview.active_campaigns)} hint={`${fmtNumber(overview.paused_campaigns)} paused · ${fmtNumber(overview.queued)} queued`} />
        <Tile category="cost" label="Cost per reply" value={fmtCredits(overview.cost_per_reply)} hint={`${fmtCredits(overview.credits_spent_30d)} spent in 30d`} />
      </div>

      {/* LAYER 2 — CHARTS */}
      <ChartGrid
        timeseries={timeseries}
        funnel={funnel}
        pacing={pacing}
        utilisation={utilisation}
        suppressionsGrowth={suppressionsGrowth}
      />

      {/* LAYER 3 — TABLES */}
      <TablesSection
        mailboxes={mailboxesTable}
        campaigns={campaignsTable}
        anomalies={anomalies}
      />

      {/* LAYER 4 — COHORT ANALYSIS */}
      <CohortPanel
        cohorts={cohorts}
        dimension={cohortDimension}
        onDimensionChange={setCohortDimension}
        loading={cohortLoading}
      />

      {/* LAYER 5 — DOMAIN HEALTH */}
      <DomainHealthPanel
        data={domainHealth}
        loading={domainHealthLoading}
        onRefresh={fetchDomainHealth}
        getToken={getToken}
        isGlobalMode={isGlobalMode}
      />

      {/* LAYER 6 — REPLY INTENT */}
      <ReplyIntentPanel data={replyIntent} loading={replyIntentLoading} onRefresh={fetchReplyIntent} />

      <div style={{ padding: 24, border: `1px dashed ${BORDER}`, borderRadius: 8, textAlign: 'center', color: MUTED, fontSize: 13, marginTop: 24 }}>
        Inbox placement and compare-period coming next.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 2 — Chart Grid
// ─────────────────────────────────────────────────────────────────────────────

const CHART_COLOURS = {
  sent: '#3B82F6',
  replies: '#10B981',
  bounces: '#F43F5E',
  complaints: '#9F1239',
  unsubs: '#F59E0B',
  today: '#8B5CF6',
  avg: '#94A3B8',
  reply_rate: '#10B981',
  bounce_rate: '#F43F5E',
};

function fmtDay(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
}

function Panel({ title, subtitle, children, height = 280 }) {
  return (
    <div style={{ background: 'white', border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{subtitle}</div>}
      </div>
      <div style={{ height, width: '100%' }}>
        {children}
      </div>
    </div>
  );
}

function ChartGrid({ timeseries, funnel, pacing, utilisation, suppressionsGrowth }) {
  // Derive reply rate per day from timeseries (sent vs replies)
  const rateData = (timeseries || []).map(r => ({
    day: r.day,
    reply_rate: r.sent > 0 ? Math.round((r.replies / r.sent) * 1000) / 10 : 0,
    bounce_rate: r.sent > 0 ? Math.round((r.bounces / r.sent) * 1000) / 10 : 0,
  }));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 24 }}>
      {/* 1. Send volume timeseries (line) */}
      <Panel title="Send volume" subtitle="Daily sends, replies and bounces — last 30 days">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={timeseries} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1ECE6" />
            <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: MUTED }} />
            <YAxis tick={{ fontSize: 11, fill: MUTED }} />
            <Tooltip labelFormatter={fmtDay} contentStyle={{ fontSize: 12, border: `1px solid ${BORDER}`, borderRadius: 6 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="sent" stroke={CHART_COLOURS.sent} strokeWidth={2} dot={false} name="Sent" />
            <Line type="monotone" dataKey="replies" stroke={CHART_COLOURS.replies} strokeWidth={2} dot={false} name="Replies" />
            <Line type="monotone" dataKey="bounces" stroke={CHART_COLOURS.bounces} strokeWidth={2} dot={false} name="Bounces" />
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      {/* 2. Reply rate over time (line + bounce rate overlay) */}
      <Panel title="Reply rate over time" subtitle="Daily reply rate and bounce rate — last 30 days">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rateData} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1ECE6" />
            <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: MUTED }} />
            <YAxis tick={{ fontSize: 11, fill: MUTED }} unit="%" />
            <Tooltip labelFormatter={fmtDay} formatter={(v) => `${v}%`} contentStyle={{ fontSize: 12, border: `1px solid ${BORDER}`, borderRadius: 6 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="reply_rate" stroke={CHART_COLOURS.reply_rate} strokeWidth={2} dot={false} name="Reply rate" />
            <Line type="monotone" dataKey="bounce_rate" stroke={CHART_COLOURS.bounce_rate} strokeWidth={2} dot={false} name="Bounce rate" />
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      {/* 3. Sequence step funnel */}
      <Panel title="Sequence funnel" subtitle="Sent vs replied by sequence step">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={funnel} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1ECE6" />
            <XAxis dataKey="step" tick={{ fontSize: 11, fill: MUTED }} label={{ value: 'Step', position: 'insideBottom', offset: -2, fontSize: 11, fill: MUTED }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: MUTED }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: MUTED }} unit="%" />
            <Tooltip contentStyle={{ fontSize: 12, border: `1px solid ${BORDER}`, borderRadius: 6 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="left" dataKey="sent" fill={CHART_COLOURS.sent} name="Sent" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="left" dataKey="replied" fill={CHART_COLOURS.replies} name="Replied" radius={[4, 4, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="reply_rate" stroke={CHART_COLOURS.reply_rate} strokeWidth={2} dot={{ r: 3 }} name="Reply rate %" />
          </ComposedChart>
        </ResponsiveContainer>
      </Panel>

      {/* 4. Hourly send pacing */}
      <Panel title="Send pacing" subtitle="Hourly distribution — today vs 7-day average">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={pacing} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1ECE6" />
            <XAxis dataKey="hour" tick={{ fontSize: 11, fill: MUTED }} tickFormatter={(h) => `${h}:00`} />
            <YAxis tick={{ fontSize: 11, fill: MUTED }} />
            <Tooltip labelFormatter={(h) => `${h}:00 UTC`} contentStyle={{ fontSize: 12, border: `1px solid ${BORDER}`, borderRadius: 6 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="avg_7d" fill={CHART_COLOURS.avg} name="7-day avg" radius={[3, 3, 0, 0]} />
            <Bar dataKey="today" fill={CHART_COLOURS.today} name="Today" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      {/* 5. Mailbox utilisation heatmap (scrollable, scale-safe up to 1000+ mailboxes) */}
      <Panel title="Mailbox utilisation" subtitle="Daily sends vs cap — last 14 days, per mailbox" height={420}>
        <UtilisationHeatmap rows={utilisation} />
      </Panel>

      {/* 6. Suppressions growth (stacked area) */}
      <Panel title="Suppressions growth" subtitle="Daily bounces, complaints and unsubscribes — last 30 days">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={suppressionsGrowth} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1ECE6" />
            <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: MUTED }} />
            <YAxis tick={{ fontSize: 11, fill: MUTED }} />
            <Tooltip labelFormatter={fmtDay} contentStyle={{ fontSize: 12, border: `1px solid ${BORDER}`, borderRadius: 6 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="bounces" stackId="1" stroke={CHART_COLOURS.bounces} fill={CHART_COLOURS.bounces} fillOpacity={0.5} name="Bounces" />
            <Area type="monotone" dataKey="complaints" stackId="1" stroke={CHART_COLOURS.complaints} fill={CHART_COLOURS.complaints} fillOpacity={0.5} name="Complaints" />
            <Area type="monotone" dataKey="unsubs" stackId="1" stroke={CHART_COLOURS.unsubs} fill={CHART_COLOURS.unsubs} fillOpacity={0.5} name="Unsubscribes" />
          </AreaChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}

const STATUS_DEFS = [
  { key: 'Healthy', dot: '#10B981' },
  { key: 'Under-used', dot: '#F59E0B' },
  { key: 'At cap', dot: '#EF4444' },
  { key: 'Dormant', dot: '#94A3B8' },
];

function FilterChips({ options, selected, onToggle, getColour }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(opt => {
        const active = selected.has(opt);
        const dot = getColour ? getColour(opt) : null;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              border: active ? '1px solid #222' : `1px solid ${BORDER}`,
              background: active ? '#222' : 'white',
              color: active ? 'white' : '#444',
              fontSize: 11,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot }} />}
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function EmailMultiSelect({ allEmails, selected, onChange, itemNounSingular = 'mailbox', itemNounPlural = 'mailboxes' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (email) => {
    const next = new Set(selected);
    if (next.has(email)) next.delete(email); else next.add(email);
    onChange(next);
  };
  const clear = () => onChange(new Set());

  const visible = search.trim()
    ? allEmails.filter(e => e.toLowerCase().includes(search.toLowerCase()))
    : allEmails;

  const summary = selected.size === 0
    ? `All ${itemNounPlural}`
    : selected.size === 1
      ? Array.from(selected)[0]
      : `${selected.size} ${itemNounPlural}`;

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 200 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '6px 10px',
          border: `1px solid ${BORDER}`, borderRadius: 5,
          background: 'white', fontSize: 12, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          color: selected.size > 0 ? '#222' : MUTED,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span style={{ marginLeft: 6, color: MUTED }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'white', border: `1px solid ${BORDER}`, borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)', zIndex: 20,
          maxHeight: 280, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: 8, borderBottom: `1px solid ${BORDER}`, display: 'flex', gap: 6 }}>
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Find ${itemNounSingular}…`}
              style={{ flex: 1, padding: '5px 8px', border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 12, outline: 'none' }}
            />
            {selected.size > 0 && (
              <button type="button" onClick={clear} style={{ fontSize: 11, color: '#8B1A1A', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 4px' }}>
                Clear
              </button>
            )}
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {visible.length === 0 ? (
              <div style={{ padding: 12, fontSize: 12, color: MUTED, textAlign: 'center' }}>No matches.</div>
            ) : visible.map(email => (
              <label key={email} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 10px', fontSize: 12, cursor: 'pointer',
                background: selected.has(email) ? '#FBF1F1' : 'transparent',
              }}>
                <input type="checkbox" checked={selected.has(email)} onChange={() => toggle(email)} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UtilisationHeatmap({ rows }) {
  const [query, setQuery] = useState('');
  const [sortMode, setSortMode] = useState('worst');
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [selectedProviders, setSelectedProviders] = useState(new Set());

  if (!rows || rows.length === 0) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: MUTED, fontSize: 13 }}>No mailbox activity yet.</div>;
  }

  const colourFor = (pct) => {
    if (pct === 0) return '#F5F2EE';
    if (pct < 25) return '#DBEAFE';
    if (pct < 50) return '#93C5FD';
    if (pct < 75) return '#3B82F6';
    if (pct < 100) return '#1D4ED8';
    return '#1E3A8A';
  };

  const statusFor = (mb) => {
    const avg = mb.days.reduce((s, d) => s + d.pct, 0) / Math.max(1, mb.days.length);
    if (avg === 0) return { colour: '#94A3B8', label: 'Dormant' };
    if (avg < 25) return { colour: '#F59E0B', label: 'Under-used' };
    if (avg > 90) return { colour: '#EF4444', label: 'At cap' };
    return { colour: '#10B981', label: 'Healthy' };
  };

  const enriched = rows.map(mb => ({
    ...mb,
    total_sent: mb.days.reduce((s, d) => s + (d.sent || 0), 0),
    avg_pct: mb.days.reduce((s, d) => s + d.pct, 0) / Math.max(1, mb.days.length),
    status: statusFor(mb),
    provider_norm: (mb.provider || 'smtp').toLowerCase(),
  }));

  // Discover provider universe from the data
  const allProviders = Array.from(new Set(enriched.map(m => m.provider_norm))).sort();
  const allEmails = enriched.map(m => m.email_address).sort();

  // Apply all filters
  const q = query.trim().toLowerCase();
  const filtered = enriched.filter(mb => {
    if (q && !mb.email_address.toLowerCase().includes(q)) return false;
    if (selectedEmails.size > 0 && !selectedEmails.has(mb.email_address)) return false;
    if (selectedStatuses.size > 0 && !selectedStatuses.has(mb.status.label)) return false;
    if (selectedProviders.size > 0 && !selectedProviders.has(mb.provider_norm)) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortMode === 'name') return a.email_address.localeCompare(b.email_address);
    if (sortMode === 'best') return b.avg_pct - a.avg_pct;
    const sev = (mb) => mb.status.label === 'At cap' ? 0 : mb.status.label === 'Dormant' ? 1 : mb.status.label === 'Under-used' ? 2 : 3;
    return sev(a) - sev(b);
  });

  const days = rows[0].days;
  const ROW_HEIGHT = 28;
  const LABEL_COL = 240;

  const toggleStatus = (s) => {
    const next = new Set(selectedStatuses);
    if (next.has(s)) next.delete(s); else next.add(s);
    setSelectedStatuses(next);
  };
  const toggleProvider = (p) => {
    const next = new Set(selectedProviders);
    if (next.has(p)) next.delete(p); else next.add(p);
    setSelectedProviders(next);
  };

  const statusColourMap = Object.fromEntries(STATUS_DEFS.map(s => [s.key, s.dot]));

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Top controls row: search + sort + counter */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexShrink: 0 }}>
        <input
          type="text"
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, padding: '6px 10px', border: `1px solid ${BORDER}`, borderRadius: 5, fontSize: 12, outline: 'none', minWidth: 120 }}
        />
        <EmailMultiSelect allEmails={allEmails} selected={selectedEmails} onChange={setSelectedEmails} />
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value)}
          style={{ padding: '6px 10px', border: `1px solid ${BORDER}`, borderRadius: 5, fontSize: 12, background: 'white' }}
        >
          <option value="worst">Problems first</option>
          <option value="best">Highest utilisation</option>
          <option value="name">Name (A-Z)</option>
        </select>
        <div style={{ fontSize: 11, color: MUTED, whiteSpace: 'nowrap' }}>
          {filtered.length} of {rows.length}
        </div>
      </div>

      {/* Chip filter rows */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 10, flexShrink: 0, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Status</span>
          <FilterChips options={STATUS_DEFS.map(s => s.key)} selected={selectedStatuses} onToggle={toggleStatus} getColour={(s) => statusColourMap[s]} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Provider</span>
          <FilterChips options={allProviders} selected={selectedProviders} onToggle={toggleProvider} />
        </div>
      </div>

      {/* Date header */}
      <div style={{ display: 'grid', gridTemplateColumns: `${LABEL_COL}px repeat(${days.length}, 1fr)`, gap: 2, paddingRight: 8, flexShrink: 0 }}>
        <div></div>
        {days.map((d, i) => (
          <div key={i} style={{ textAlign: 'center', color: MUTED, fontSize: 10, paddingBottom: 6 }}>
            {fmtDay(d.day)}
          </div>
        ))}
      </div>

      {/* Scrollable rows */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 100 }}>
        {sorted.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: MUTED, fontSize: 12 }}>No matches.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: `${LABEL_COL}px repeat(${days.length}, 1fr)`, gap: 2 }}>
            {sorted.map(mb => (
              <React.Fragment key={mb.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px', height: ROW_HEIGHT, fontSize: 12, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  <span title={mb.status.label} style={{ width: 8, height: 8, borderRadius: '50%', background: mb.status.colour, flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${mb.email_address} · ${mb.total_sent} sends · ${Math.round(mb.avg_pct)}% avg cap`}>
                    {mb.email_address}
                  </span>
                </div>
                {mb.days.map((d, i) => (
                  <div
                    key={i}
                    title={`${fmtDay(d.day)} · ${d.sent}/${d.cap} (${d.pct}%)`}
                    style={{
                      background: colourFor(d.pct),
                      height: ROW_HEIGHT - 6,
                      alignSelf: 'center',
                      borderRadius: 3,
                      cursor: 'help',
                    }}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 3 — Tables (mailbox health, campaigns, anomalies)
// ─────────────────────────────────────────────────────────────────────────────

function severityColour(sev) {
  if (sev === 'high') return { bg: '#FEE2E2', text: '#991B1B', dot: '#DC2626' };
  if (sev === 'medium') return { bg: '#FEF3C7', text: '#92400E', dot: '#D97706' };
  return { bg: '#E0E7FF', text: '#3730A3', dot: '#4F46E5' };
}

function AnomalyBanner({ anomalies }) {
  const [collapsed, setCollapsed] = useState(false);
  const top = anomalies.slice(0, 3);
  const remaining = anomalies.length - top.length;
  const worstSev = anomalies.some(a => a.severity === 'high') ? 'high' : 'medium';
  const c = severityColour(worstSev);

  return (
    <div style={{
      background: c.bg, borderRadius: 10, padding: '12px 16px', marginBottom: 16,
      border: `1px solid ${c.dot}33`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.dot }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: c.text }}>
            {anomalies.length === 1 ? '1 anomaly detected' : `${anomalies.length} anomalies detected`} in the last 7 days
          </span>
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{ background: 'transparent', border: 'none', color: c.text, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
        >
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>
      {!collapsed && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {top.map((a, i) => {
            const ac = severityColour(a.severity);
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: '#222' }}>
                <span style={{
                  background: ac.dot, color: 'white', fontSize: 10, fontWeight: 600,
                  padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: 0.3, minWidth: 50, textAlign: 'center',
                }}>
                  {a.severity}
                </span>
                <span style={{ color: MUTED, minWidth: 70, fontSize: 11 }}>{fmtDay(a.day)}</span>
                <span>{a.message}</span>
              </div>
            );
          })}
          {remaining > 0 && (
            <div style={{ fontSize: 11, color: c.text, opacity: 0.7, marginTop: 4 }}>
              + {remaining} more — see Anomaly feed below
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScoreBar({ score, height = 6 }) {
  let bar = '#10B981';
  if (score == null) bar = '#94A3B8';
  else if (score < 60) bar = '#EF4444';
  else if (score < 80) bar = '#F59E0B';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height, background: '#F1F5F9', borderRadius: 3, overflow: 'hidden', minWidth: 40 }}>
        <div style={{ height: '100%', width: `${score || 0}%`, background: bar }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#222', minWidth: 28, textAlign: 'right' }}>{score == null ? '—' : score}</span>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: { bg: '#DCFCE7', text: '#166534' },
    paused: { bg: '#FEF3C7', text: '#92400E' },
    completed: { bg: '#E0E7FF', text: '#3730A3' },
    draft: { bg: '#F1F5F9', text: '#475569' },
    error: { bg: '#FEE2E2', text: '#991B1B' },
    disconnected: { bg: '#FEE2E2', text: '#991B1B' },
  };
  const c = map[status] || { bg: '#F1F5F9', text: '#475569' };
  return (
    <span style={{
      background: c.bg, color: c.text, fontSize: 11, fontWeight: 500,
      padding: '2px 8px', borderRadius: 999, textTransform: 'capitalize',
    }}>
      {status || 'unknown'}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FilterableTable — reusable component for all 3 tables
//
// Props:
//   title, subtitle      — panel header
//   rows                 — array of row objects
//   columns              — [{ key, label, align, render, sortable, width }]
//   defaultSort          — { key, dir }
//   searchableFields     — array of field names to text-search
//   multiSelectField     — field name to power the "select specific items" dropdown (e.g. 'email_address')
//   multiSelectLabel     — label for the multi-select dropdown ("Mailboxes")
//   filterChipGroups     — [{ key, label, options: [{ value, label, dot? }], getValue: (row) => ... }]
//   emptyMessage         — text shown when no rows
//   renderCard           — optional: if provided, renders cards instead of table (for anomaly feed)
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function FilterableTable({
  title, subtitle,
  rows = [],
  columns = [],
  defaultSort,
  searchableFields = [],
  multiSelectField,
  multiSelectLabel,
  filterChipGroups = [],
  emptyMessage = 'No matches.',
  renderCard,
}) {
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState(defaultSort?.key || null);
  const [sortDir, setSortDir] = useState(defaultSort?.dir || 'desc');
  const [chipSelections, setChipSelections] = useState(() => {
    const init = {};
    filterChipGroups.forEach(g => { init[g.key] = new Set(); });
    return init;
  });
  const [multiSelected, setMultiSelected] = useState(new Set());
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);

  // Reset to page 0 whenever filters change
  React.useEffect(() => { setPage(0); }, [query, chipSelections, multiSelected, pageSize]);

  // Apply text search
  const q = query.trim().toLowerCase();
  let filtered = rows;
  if (q && searchableFields.length > 0) {
    filtered = filtered.filter(row =>
      searchableFields.some(f => String(row[f] || '').toLowerCase().includes(q))
    );
  }

  // Apply multi-select
  if (multiSelectField && multiSelected.size > 0) {
    filtered = filtered.filter(row => multiSelected.has(row[multiSelectField]));
  }

  // Apply chip filters
  filterChipGroups.forEach(group => {
    const sel = chipSelections[group.key];
    if (sel && sel.size > 0) {
      filtered = filtered.filter(row => sel.has(group.getValue(row)));
    }
  });

  // Sort
  let sorted = filtered;
  if (sortBy) {
    sorted = [...filtered].sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }

  // Paginate
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * pageSize;
  const paged = sorted.slice(pageStart, pageStart + pageSize);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('desc'); }
  };
  const toggleChip = (groupKey, value) => {
    setChipSelections(prev => {
      const next = { ...prev };
      const s = new Set(next[groupKey]);
      if (s.has(value)) s.delete(value); else s.add(value);
      next[groupKey] = s;
      return next;
    });
  };
  const clearAllFilters = () => {
    setQuery('');
    setMultiSelected(new Set());
    const cleared = {};
    filterChipGroups.forEach(g => { cleared[g.key] = new Set(); });
    setChipSelections(cleared);
  };

  const anyFilterActive = q || multiSelected.size > 0 || Object.values(chipSelections).some(s => s.size > 0);

  // Multi-select universe
  const multiSelectUniverse = multiSelectField
    ? Array.from(new Set(rows.map(r => r[multiSelectField]).filter(Boolean))).sort()
    : [];

  const Th = ({ col }) => (
    <th
      onClick={() => col.sortable !== false && toggleSort(col.key)}
      style={{
        textAlign: col.align || 'left', padding: '8px 10px', fontSize: 11, color: MUTED,
        textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600,
        cursor: col.sortable !== false ? 'pointer' : 'default',
        whiteSpace: 'nowrap', userSelect: 'none',
        width: col.width || undefined,
      }}
    >
      {col.label}{sortBy === col.key && (sortDir === 'asc' ? ' ↑' : ' ↓')}
    </th>
  );

  return (
    <div style={{ background: 'white', border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{subtitle}</div>}
      </div>

      {/* Top control row: search + multi-select + counter + clear */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        {searchableFields.length > 0 && (
          <input
            type="text"
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ flex: '1 1 180px', padding: '6px 10px', border: `1px solid ${BORDER}`, borderRadius: 5, fontSize: 12, outline: 'none', minWidth: 120 }}
          />
        )}
        {multiSelectField && (
          <EmailMultiSelect
            allEmails={multiSelectUniverse}
            selected={multiSelected}
            onChange={setMultiSelected}
            itemNounSingular={(multiSelectLabel || 'item').replace(/s$/i, '').toLowerCase()}
            itemNounPlural={(multiSelectLabel || 'items').toLowerCase()}
          />
        )}
        <div style={{ fontSize: 11, color: MUTED, whiteSpace: 'nowrap' }}>
          {sorted.length === rows.length ? `${rows.length} total` : `${sorted.length} of ${rows.length}`}
        </div>
        {anyFilterActive && (
          <button
            onClick={clearAllFilters}
            style={{ fontSize: 11, color: '#8B1A1A', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 4px' }}
          >
            Clear all
          </button>
        )}
      </div>

      {/* Chip filter rows */}
      {filterChipGroups.length > 0 && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          {filterChipGroups.map(group => (
            <div key={group.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{group.label}</span>
              <FilterChips
                options={group.options.map(o => o.value)}
                selected={chipSelections[group.key] || new Set()}
                onToggle={(v) => toggleChip(group.key, v)}
                getColour={(v) => {
                  const opt = group.options.find(o => o.value === v);
                  return opt?.dot;
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Body: either table or cards */}
      <div style={{ overflowX: 'auto', maxHeight: 520, overflowY: 'auto' }}>
        {paged.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: MUTED, fontSize: 13 }}>{emptyMessage}</div>
        ) : renderCard ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {paged.map((row, i) => renderCard(row, i))}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1, borderBottom: `1px solid ${BORDER}` }}>
              <tr>{columns.map(col => <Th key={col.key} col={col} />)}</tr>
            </thead>
            <tbody>
              {paged.map((row, i) => (
                <tr key={row.id ?? i} style={{ borderBottom: '1px solid #F5F2EE' }}>
                  {columns.map(col => (
                    <td key={col.key} style={{
                      padding: '8px 10px',
                      textAlign: col.align || 'left',
                      maxWidth: col.maxWidth || undefined,
                      overflow: col.maxWidth ? 'hidden' : undefined,
                      textOverflow: col.maxWidth ? 'ellipsis' : undefined,
                      whiteSpace: col.maxWidth ? 'nowrap' : undefined,
                    }}>
                      {col.render ? col.render(row) : row[col.key] ?? '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination footer */}
      {sorted.length > pageSize && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${BORDER}`, fontSize: 12, color: MUTED }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value))}
              style={{ padding: '4px 6px', border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 12, background: 'white' }}
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>{pageStart + 1}–{Math.min(pageStart + pageSize, sorted.length)} of {sorted.length}</span>
            <button
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              style={{ padding: '4px 10px', border: `1px solid ${BORDER}`, borderRadius: 4, background: 'white', fontSize: 12, cursor: safePage === 0 ? 'not-allowed' : 'pointer', opacity: safePage === 0 ? 0.4 : 1 }}
            >
              Previous
            </button>
            <span style={{ fontSize: 12 }}>Page {safePage + 1} of {totalPages}</span>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
              disabled={safePage >= totalPages - 1}
              style={{ padding: '4px 10px', border: `1px solid ${BORDER}`, borderRadius: 4, background: 'white', fontSize: 12, cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: safePage >= totalPages - 1 ? 0.4 : 1 }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The three concrete tables wire FilterableTable with their specific configs
// ─────────────────────────────────────────────────────────────────────────────

function MailboxHealthTable({ mailboxes }) {
  const enriched = mailboxes.map(m => ({
    ...m,
    provider_norm: (m.provider || 'smtp').toLowerCase(),
    score_band: m.deliverability_score < 60 ? 'Critical' : m.deliverability_score < 80 ? 'Warning' : 'Healthy',
  }));

  const allProviders = Array.from(new Set(enriched.map(m => m.provider_norm))).sort();
  const allStatuses = Array.from(new Set(enriched.map(m => m.status).filter(Boolean))).sort();

  const columns = [
    { key: 'email_address', label: 'Mailbox', maxWidth: 220, render: (m) => <span title={m.email_address}>{m.email_address}</span> },
    { key: 'status', label: 'Status', render: (m) => <StatusPill status={m.status} /> },
    { key: 'provider', label: 'Provider', render: (m) => <span style={{ textTransform: 'capitalize', color: '#444' }}>{m.provider || '—'}</span> },
    { key: 'deliverability_score', label: 'Score', width: 140, render: (m) => <ScoreBar score={m.deliverability_score} /> },
    { key: 'sent_30d', label: 'Sent 30d', align: 'right', render: (m) => fmtNumber(m.sent_30d) },
    { key: 'reply_rate', label: 'Reply %', align: 'right', render: (m) => `${m.reply_rate}%` },
    { key: 'bounce_rate', label: 'Bounce %', align: 'right', render: (m) => <span style={{ color: m.bounce_rate > 5 ? '#B91C1C' : '#444' }}>{m.bounce_rate}%</span> },
    { key: 'cap_pct', label: 'Cap today', align: 'right', render: (m) => `${m.cap_pct}%` },
    { key: 'warmup_pct', label: 'Warmup', align: 'right', render: (m) => m.warmup_enabled ? <ScoreBar score={m.warmup_pct} height={4} /> : <span style={{ color: MUTED, fontSize: 11 }}>off</span> },
  ];

  return (
    <FilterableTable
      title="Mailbox health"
      subtitle="Sorted by deliverability score — worst first"
      rows={enriched}
      columns={columns}
      defaultSort={{ key: 'deliverability_score', dir: 'asc' }}
      searchableFields={['email_address']}
      multiSelectField="email_address"
      multiSelectLabel="Mailboxes"
      filterChipGroups={[
        {
          key: 'status', label: 'Status',
          options: allStatuses.map(s => ({ value: s, label: s })),
          getValue: (m) => m.status,
        },
        {
          key: 'provider', label: 'Provider',
          options: allProviders.map(p => ({ value: p, label: p })),
          getValue: (m) => m.provider_norm,
        },
        {
          key: 'score_band', label: 'Score band',
          options: [
            { value: 'Critical', label: 'Critical', dot: '#EF4444' },
            { value: 'Warning', label: 'Warning', dot: '#F59E0B' },
            { value: 'Healthy', label: 'Healthy', dot: '#10B981' },
          ],
          getValue: (m) => m.score_band,
        },
      ]}
      emptyMessage="No mailboxes match these filters."
    />
  );
}

function CampaignsTable({ campaigns }) {
  const enriched = campaigns.map(c => ({
    ...c,
    perf_band: c.reply_rate >= 5 ? 'High' : c.reply_rate >= 1 ? 'Medium' : 'Low',
  }));

  const allStatuses = Array.from(new Set(enriched.map(c => c.status).filter(Boolean))).sort();

  const columns = [
    { key: 'name', label: 'Campaign', maxWidth: 280, render: (c) => <span title={c.name}>{c.name}</span> },
    { key: 'status', label: 'Status', render: (c) => <StatusPill status={c.status} /> },
    { key: 'total_contacts', label: 'Contacts', align: 'right', render: (c) => fmtNumber(c.total_contacts) },
    { key: 'sent_count', label: 'Sent', align: 'right', render: (c) => fmtNumber(c.sent_count) },
    { key: 'reply_rate', label: 'Reply %', align: 'right', render: (c) => <span style={{ fontWeight: 500, color: c.reply_rate >= 5 ? '#065F46' : '#444' }}>{c.reply_rate}%</span> },
    { key: 'bounce_rate', label: 'Bounce %', align: 'right', render: (c) => <span style={{ color: c.bounce_rate > 5 ? '#B91C1C' : '#444' }}>{c.bounce_rate}%</span> },
    { key: 'completion_pct', label: 'Completion', align: 'right', render: (c) => <ScoreBar score={c.completion_pct} height={4} /> },
    { key: 'started_at', label: 'Started', render: (c) => <span style={{ color: MUTED, fontSize: 12 }}>{c.started_at ? fmtDay(c.started_at) : '—'}</span> },
  ];

  return (
    <FilterableTable
      title="Campaign performance"
      subtitle="Sorted by reply rate"
      rows={enriched}
      columns={columns}
      defaultSort={{ key: 'reply_rate', dir: 'desc' }}
      searchableFields={['name']}
      multiSelectField="name"
      multiSelectLabel="Campaigns"
      filterChipGroups={[
        {
          key: 'status', label: 'Status',
          options: allStatuses.map(s => ({ value: s, label: s })),
          getValue: (c) => c.status,
        },
        {
          key: 'perf_band', label: 'Performance',
          options: [
            { value: 'High', label: 'High (≥5%)', dot: '#10B981' },
            { value: 'Medium', label: 'Medium (1-5%)', dot: '#F59E0B' },
            { value: 'Low', label: 'Low (<1%)', dot: '#EF4444' },
          ],
          getValue: (c) => c.perf_band,
        },
      ]}
      emptyMessage="No campaigns match these filters."
    />
  );
}

function AnomalyFeed({ anomalies }) {
  const enriched = anomalies.map((a, i) => ({ ...a, _id: i, abs_z: Math.abs(a.z || 0) }));

  return (
    <FilterableTable
      title="Anomaly feed"
      subtitle="Days that deviate >2σ from the 30-day baseline (last 7 days)"
      rows={enriched}
      columns={[]}
      defaultSort={{ key: 'abs_z', dir: 'desc' }}
      searchableFields={['message']}
      filterChipGroups={[
        {
          key: 'severity', label: 'Severity',
          options: [
            { value: 'high', label: 'High', dot: '#DC2626' },
            { value: 'medium', label: 'Medium', dot: '#D97706' },
          ],
          getValue: (a) => a.severity,
        },
        {
          key: 'type', label: 'Type',
          options: [
            { value: 'bounce_spike', label: 'Bounce spike', dot: '#EF4444' },
            { value: 'reply_drop', label: 'Reply drop', dot: '#F59E0B' },
            { value: 'send_spike', label: 'Send spike', dot: '#3B82F6' },
            { value: 'send_drop', label: 'Send drop', dot: '#94A3B8' },
          ],
          getValue: (a) => a.type,
        },
      ]}
      emptyMessage="No anomalies detected. Sending volumes, reply rates and bounce rates are within normal ranges."
      renderCard={(a) => {
        const c = severityColour(a.severity);
        return (
          <div key={a._id} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', background: c.bg, borderRadius: 6,
          }}>
            <span style={{
              background: c.dot, color: 'white', fontSize: 10, fontWeight: 600,
              padding: '2px 7px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: 0.3, minWidth: 50, textAlign: 'center',
            }}>
              {a.severity}
            </span>
            <span style={{ color: MUTED, minWidth: 70, fontSize: 11 }}>{fmtDay(a.day)}</span>
            <span style={{ fontSize: 12, color: '#222', flex: 1 }}>{a.message}</span>
            <span style={{ fontSize: 11, color: MUTED }}>z = {a.z}</span>
          </div>
        );
      }}
    />
  );
}

function TablesSection({ mailboxes, campaigns, anomalies }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <MailboxHealthTable mailboxes={mailboxes} />
      <CampaignsTable campaigns={campaigns} />
      <AnomalyFeed anomalies={anomalies} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 4 — Cohort analysis panel
// ─────────────────────────────────────────────────────────────────────────────

const COHORT_DIMS = [
  { key: 'sector',   label: 'Sector' },
  { key: 'persona',  label: 'Sender persona' },
  { key: 'title',    label: 'Job title' },
  { key: 'hour',     label: 'Hour of day' },
  { key: 'step',     label: 'Sequence step' },
  { key: 'provider', label: 'Provider' },
];

function CohortPanel({ cohorts, dimension, onDimensionChange, loading }) {
  const rows = cohorts?.rows || [];
  const dimensionLabel = cohorts?.label || COHORT_DIMS.find(d => d.key === dimension)?.label || 'Cohort';

  // For hour dimension, sort numerically and ensure all hours represented in 0-23 order if asked
  let displayRows = rows;
  if (dimension === 'hour') {
    displayRows = [...rows].sort((a, b) => parseInt(a.cohort) - parseInt(b.cohort));
  } else if (dimension === 'step') {
    displayRows = [...rows].sort((a, b) => {
      const ai = a.cohort === 'unknown' ? 999 : parseInt(a.cohort);
      const bi = b.cohort === 'unknown' ? 999 : parseInt(b.cohort);
      return ai - bi;
    });
  }

  // Format hour cohort as "14:00"
  const fmtCohort = (c) => {
    if (dimension === 'hour' && c !== 'unknown') return `${c}:00`;
    if (dimension === 'step' && c !== 'unknown') return `Step ${c}`;
    return c;
  };

  return (
    <div style={{ background: 'white', border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16, marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>Cohort analysis</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
            Reply rate and bounce rate by {dimensionLabel.toLowerCase()} — last 30 days
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {COHORT_DIMS.map(d => (
            <button
              key={d.key}
              type="button"
              onClick={() => onDimensionChange(d.key)}
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                border: dimension === d.key ? '1px solid #222' : `1px solid ${BORDER}`,
                background: dimension === d.key ? '#222' : 'white',
                color: dimension === d.key ? 'white' : '#444',
                fontSize: 12,
                cursor: 'pointer',
                fontWeight: dimension === d.key ? 500 : 400,
              }}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: MUTED, fontSize: 13 }}>Loading cohorts…</div>
      ) : displayRows.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: MUTED, fontSize: 13 }}>No cohort data yet for this dimension.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Left chart: reply rate per cohort, capped to top N (scrollable when expanded) */}
          <CohortBarChart
            rows={displayRows}
            dimension={dimension}
            dimensionLabel={dimensionLabel}
            fmtCohort={fmtCohort}
          />

          {/* Right: searchable / sortable / paginated detail table via FilterableTable */}
          <CohortDetailTable
            rows={displayRows}
            dimension={dimension}
            dimensionLabel={dimensionLabel}
            fmtCohort={fmtCohort}
          />
        </div>
      )}
    </div>
  );
}

// Top-N cohort chart with "Show all" toggle. Default 15 bars + "Other" lump row.
function CohortBarChart({ rows, dimension, dimensionLabel, fmtCohort }) {
  const [showAll, setShowAll] = useState(false);
  const TOP_N = 15;

  // For hour and step we want all bars in natural order; capping doesn't make sense there
  const orderedDims = ['hour', 'step'];
  const naturalOrder = orderedDims.includes(dimension);

  let chartRows;
  let otherCount = 0;
  if (naturalOrder || showAll || rows.length <= TOP_N) {
    chartRows = rows.map(r => ({ ...r, name: fmtCohort(r.cohort) }));
  } else {
    // Top N by sent volume
    const sortedBySent = [...rows].sort((a, b) => b.sent - a.sent);
    const top = sortedBySent.slice(0, TOP_N);
    const rest = sortedBySent.slice(TOP_N);
    otherCount = rest.length;
    if (rest.length > 0) {
      const otherSent = rest.reduce((s, r) => s + r.sent, 0);
      const otherReplies = rest.reduce((s, r) => s + r.replies, 0);
      const otherBounces = rest.reduce((s, r) => s + r.bounces, 0);
      top.push({
        cohort: `__other__`,
        name: `Other (${rest.length})`,
        sent: otherSent,
        replies: otherReplies,
        bounces: otherBounces,
        reply_rate: otherSent > 0 ? Math.round((otherReplies / otherSent) * 1000) / 10 : 0,
        bounce_rate: otherSent > 0 ? Math.round((otherBounces / otherSent) * 1000) / 10 : 0,
      });
    }
    chartRows = top.map(r => ({ ...r, name: r.name || fmtCohort(r.cohort) }));
  }

  // Each bar ~22px high + 60px padding. Cap height at 440px with internal scroll if more.
  const naturalHeight = chartRows.length * 26 + 60;
  const capped = naturalHeight > 440;
  const chartHeight = capped ? Math.max(440, chartRows.length * 26 + 60) : Math.max(180, naturalHeight);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
          Reply rate by {dimensionLabel.toLowerCase()}
        </div>
        {!naturalOrder && rows.length > TOP_N && (
          <button
            onClick={() => setShowAll(s => !s)}
            style={{ background: 'transparent', border: 'none', color: '#8B1A1A', fontSize: 11, cursor: 'pointer', padding: 0 }}
          >
            {showAll ? `Show top ${TOP_N}` : `Show all ${rows.length}`}
          </button>
        )}
      </div>
      <div style={{ maxHeight: 440, overflowY: capped ? 'auto' : 'visible' }}>
        <div style={{ height: chartHeight, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartRows} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1ECE6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: MUTED }} unit="%" />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#444' }} width={140} />
              <Tooltip
                contentStyle={{ fontSize: 12, border: `1px solid ${BORDER}`, borderRadius: 6 }}
                formatter={(v, k) => k === 'reply_rate' ? [`${v}%`, 'Reply rate'] : v}
              />
              <Bar dataKey="reply_rate" fill={CHART_COLOURS.reply_rate} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {otherCount > 0 && !showAll && (
        <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>
          {otherCount} more cohort{otherCount === 1 ? '' : 's'} grouped under "Other" — click "Show all" to expand.
        </div>
      )}
    </div>
  );
}

// Detail table using FilterableTable for search/sort/paginate
function CohortDetailTable({ rows, dimension, dimensionLabel, fmtCohort }) {
  const enriched = rows.map((r, i) => ({
    ...r,
    _id: i,
    cohort_display: fmtCohort(r.cohort),
  }));

  const columns = [
    { key: 'cohort_display', label: dimensionLabel, maxWidth: 200, render: (r) => <span style={{ textTransform: 'capitalize' }} title={r.cohort}>{r.cohort_display}</span> },
    { key: 'sent', label: 'Sent', align: 'right', render: (r) => fmtNumber(r.sent) },
    { key: 'reply_rate', label: 'Reply %', align: 'right', render: (r) => <span style={{ fontWeight: 500, color: r.reply_rate >= 5 ? '#065F46' : '#444' }}>{r.reply_rate}%</span> },
    { key: 'bounce_rate', label: 'Bounce %', align: 'right', render: (r) => <span style={{ color: r.bounce_rate > 5 ? '#B91C1C' : '#444' }}>{r.bounce_rate}%</span> },
  ];

  return (
    <div>
      <div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 8 }}>Detail</div>
      <FilterableTable
        title=""
        rows={enriched}
        columns={columns}
        defaultSort={{ key: 'sent', dir: 'desc' }}
        searchableFields={['cohort_display']}
        emptyMessage="No cohort rows match."
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 5 — Domain health panel
// ─────────────────────────────────────────────────────────────────────────────

function authBadge(status) {
  const map = {
    pass: { bg: '#DCFCE7', text: '#166534', label: 'Pass' },
    fail: { bg: '#FEE2E2', text: '#991B1B', label: 'Fail' },
    unknown: { bg: '#F1F5F9', text: '#475569', label: '—' },
  };
  return map[status] || map.unknown;
}

function gradeBadge(grade) {
  const map = {
    good:        { bg: '#DCFCE7', text: '#166534', label: 'Healthy' },
    partial:     { bg: '#FEF3C7', text: '#92400E', label: 'Partial' },
    poor:        { bg: '#FED7AA', text: '#9A3412', label: 'Poor' },
    missing:     { bg: '#FEE2E2', text: '#991B1B', label: 'Missing' },
    blacklisted: { bg: '#FEE2E2', text: '#991B1B', label: 'Blacklisted' },
  };
  return map[grade] || { bg: '#F1F5F9', text: '#475569', label: grade };
}

function DomainHealthSummary({ summary }) {
  if (!summary) return null;
  const tile = (label, n, colour) => (
    <div style={{ flex: 1, background: colour.bg, padding: '12px 16px', borderRadius: 8, minWidth: 120 }}>
      <div style={{ fontSize: 10, color: colour.label, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: colour.value, marginTop: 4 }}>{fmtNumber(n)}</div>
    </div>
  );
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
      {tile('Total domains', summary.total, { bg: '#F1F5F9', label: '#475569', value: '#0F172A' })}
      {tile('Healthy', summary.healthy, { bg: '#DCFCE7', label: '#166534', value: '#065F46' })}
      {tile('Partial', summary.partial, { bg: '#FEF3C7', label: '#92400E', value: '#92400E' })}
      {tile('Failing', summary.failing, { bg: '#FED7AA', label: '#9A3412', value: '#9A3412' })}
      {tile('Blacklisted', summary.blacklisted, { bg: '#FEE2E2', label: '#991B1B', value: '#991B1B' })}
    </div>
  );
}

function DomainRow({ row, onRefresh, getToken, isGlobalMode }) {
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const refresh = async () => {
    if (!isGlobalMode) return;
    setRefreshing(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/admin/metrics/domain-health/${encodeURIComponent(row.domain)}/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) onRefresh();
    } catch (_) {}
    finally { setRefreshing(false); }
  };

  const grade = gradeBadge(row.health_grade);
  const spf = authBadge(row.spf_status);
  const dkim = authBadge(row.dkim_status);
  const dmarc = authBadge(row.dmarc_status);

  const listedHosts = (row.blacklists || []).filter(b => b.listed);

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: 12, marginBottom: 8, background: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#222', minWidth: 200 }}>{row.domain}</div>
        <span style={{
          background: grade.bg, color: grade.text, fontSize: 11, fontWeight: 500,
          padding: '2px 8px', borderRadius: 999,
        }}>{grade.label}</span>
        <div style={{ display: 'flex', gap: 6, fontSize: 11 }}>
          <span style={{ background: spf.bg, color: spf.text, padding: '2px 8px', borderRadius: 4 }}>SPF · {spf.label}</span>
          <span style={{ background: dkim.bg, color: dkim.text, padding: '2px 8px', borderRadius: 4 }}>DKIM · {dkim.label}</span>
          <span style={{ background: dmarc.bg, color: dmarc.text, padding: '2px 8px', borderRadius: 4 }}>
            DMARC · {dmarc.label}{row.dmarc_policy ? ` (${row.dmarc_policy})` : ''}
          </span>
        </div>
        <div style={{ fontSize: 11, color: MUTED }}>
          {row.blacklist_count > 0
            ? <span style={{ color: '#B91C1C', fontWeight: 600 }}>{row.blacklist_count} blacklist{row.blacklist_count === 1 ? '' : 's'}</span>
            : <span>0 blacklists</span>}
        </div>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setExpanded(!expanded)}
          style={{ background: 'transparent', border: 'none', color: '#8B1A1A', fontSize: 12, cursor: 'pointer', padding: 0 }}
        >
          {expanded ? 'Hide details' : 'Details'}
        </button>
        {isGlobalMode && (
          <button
            onClick={refresh}
            disabled={refreshing}
            style={{ background: 'white', border: `1px solid ${BORDER}`, padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', opacity: refreshing ? 0.5 : 1 }}
          >
            {refreshing ? 'Checking…' : 'Re-check'}
          </button>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 6 }}>Blacklists</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
            {(row.blacklists || []).map((b, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 8px', fontSize: 11,
                background: b.listed ? '#FEE2E2' : '#F1F5F9',
                color: b.listed ? '#991B1B' : '#475569',
                borderRadius: 3,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: b.listed ? '#DC2626' : '#10B981' }} />
                <span style={{ fontWeight: b.listed ? 600 : 400 }}>{b.name}</span>
                {b.listed && <span style={{ marginLeft: 'auto' }}>LISTED</span>}
              </div>
            ))}
          </div>
          {row.last_checked_at && (
            <div style={{ fontSize: 11, color: MUTED, marginTop: 8 }}>
              Last checked: {new Date(row.last_checked_at).toLocaleString('en-GB')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DomainHealthPanel({ data, loading, onRefresh, getToken, isGlobalMode }) {
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [query, setQuery] = useState('');

  const rows = data?.domains || [];
  const summary = data?.summary;

  const allStatuses = ['good', 'partial', 'poor', 'missing', 'blacklisted'];
  const statusOptions = [
    { value: 'good', label: 'Healthy', dot: '#10B981' },
    { value: 'partial', label: 'Partial', dot: '#F59E0B' },
    { value: 'poor', label: 'Poor', dot: '#F97316' },
    { value: 'missing', label: 'Missing', dot: '#EF4444' },
    { value: 'blacklisted', label: 'Blacklisted', dot: '#DC2626' },
  ];

  let filtered = rows;
  if (query.trim()) {
    const q = query.toLowerCase();
    filtered = filtered.filter(r => r.domain.toLowerCase().includes(q));
  }
  if (statusFilter.size > 0) {
    filtered = filtered.filter(r => statusFilter.has(r.health_grade));
  }

  const toggleStatus = (v) => {
    const next = new Set(statusFilter);
    if (next.has(v)) next.delete(v); else next.add(v);
    setStatusFilter(next);
  };

  return (
    <div style={{ background: 'white', border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16, marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>Domain health</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
            SPF, DKIM, DMARC and 9 DNSBL checks per sending domain. Refreshed daily.
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          style={{ background: 'white', border: `1px solid ${BORDER}`, padding: '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer', opacity: loading ? 0.5 : 1 }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {loading && !data ? (
        <div style={{ padding: 32, textAlign: 'center', color: MUTED, fontSize: 13 }}>Running DNS checks across sending domains…</div>
      ) : (
        <>
          <DomainHealthSummary summary={summary} />

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search domains…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ flex: '1 1 200px', padding: '6px 10px', border: `1px solid ${BORDER}`, borderRadius: 5, fontSize: 12, outline: 'none' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Status</span>
              <FilterChips
                options={allStatuses}
                selected={statusFilter}
                onToggle={toggleStatus}
                getColour={(v) => statusOptions.find(o => o.value === v)?.dot}
              />
            </div>
            <div style={{ fontSize: 11, color: MUTED, whiteSpace: 'nowrap' }}>
              {filtered.length === rows.length ? `${rows.length} total` : `${filtered.length} of ${rows.length}`}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: MUTED, fontSize: 13 }}>
              {rows.length === 0 ? 'No sending domains found.' : 'No domains match these filters.'}
            </div>
          ) : (
            <div style={{ maxHeight: 600, overflowY: 'auto' }}>
              {filtered.map(row => (
                <DomainRow
                  key={row.domain}
                  row={row}
                  onRefresh={onRefresh}
                  getToken={getToken}
                  isGlobalMode={isGlobalMode}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 6 — Reply intent classification panel
// ─────────────────────────────────────────────────────────────────────────────

const INTENT_LABEL_META = {
  interested:          { display: 'Interested',       colour: '#10B981', priority: 1 },
  question:            { display: 'Question',         colour: '#3B82F6', priority: 2 },
  referral:            { display: 'Referral',         colour: '#06B6D4', priority: 3 },
  out_of_office:       { display: 'Out of office',    colour: '#F59E0B', priority: 4 },
  not_interested:      { display: 'Not interested',   colour: '#F43F5E', priority: 5 },
  unsubscribe_request: { display: 'Unsubscribe',      colour: '#9F1239', priority: 6 },
  other:               { display: 'Other',            colour: '#64748B', priority: 7 },
};

function ReplyIntentPanel({ data, loading, onRefresh }) {
  if (loading && !data) {
    return (
      <div style={{ background: 'white', border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16, marginBottom: 24 }}>
        <div style={{ padding: 32, textAlign: 'center', color: MUTED, fontSize: 13 }}>Loading reply intent…</div>
      </div>
    );
  }
  if (!data) return null;

  const breakdown = (data.breakdown || []).slice().sort((a, b) => {
    return (INTENT_LABEL_META[a.label]?.priority || 99) - (INTENT_LABEL_META[b.label]?.priority || 99);
  });

  const positiveLabels = ['interested', 'question', 'referral'];
  const positive = breakdown.filter(b => positiveLabels.includes(b.label)).reduce((s, b) => s + b.count, 0);
  const positivePct = data.classified > 0 ? Math.round((positive / data.classified) * 1000) / 10 : 0;

  // Donut chart data — exclude zero rows for cleaner pie
  const chartData = breakdown.filter(b => b.count > 0).map(b => ({
    name: INTENT_LABEL_META[b.label]?.display || b.label,
    value: b.count,
    fill: INTENT_LABEL_META[b.label]?.colour || '#94A3B8',
    pct: b.pct,
  }));

  return (
    <div style={{ background: 'white', border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16, marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>Reply intent</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
            Classification of inbound replies — last 30 days. {data.unclassified > 0 && `${data.unclassified} pending classification.`}
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          style={{ background: 'white', border: `1px solid ${BORDER}`, padding: '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer', opacity: loading ? 0.5 : 1 }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Summary tiles */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, background: '#F1F5F9', padding: '12px 16px', borderRadius: 8, minWidth: 120 }}>
          <div style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Total replies</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', marginTop: 4 }}>{fmtNumber(data.total)}</div>
        </div>
        <div style={{ flex: 1, background: '#DCFCE7', padding: '12px 16px', borderRadius: 8, minWidth: 120 }}>
          <div style={{ fontSize: 10, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Positive replies</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#065F46', marginTop: 4 }}>{fmtNumber(positive)} <span style={{ fontSize: 12, fontWeight: 500, color: '#065F46' }}>({positivePct}%)</span></div>
          <div style={{ fontSize: 10, color: '#065F46', marginTop: 2 }}>Interested + Question + Referral</div>
        </div>
        <div style={{ flex: 1, background: '#ECFDF5', padding: '12px 16px', borderRadius: 8, minWidth: 120 }}>
          <div style={{ fontSize: 10, color: '#065F46', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Interested rate</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#065F46', marginTop: 4 }}>{data.interested_rate}%</div>
        </div>
        <div style={{ flex: 1, background: '#F1F5F9', padding: '12px 16px', borderRadius: 8, minWidth: 120 }}>
          <div style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Classified</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', marginTop: 4 }}>{fmtNumber(data.classified)}</div>
        </div>
      </div>

      {data.classified === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: MUTED, fontSize: 13 }}>
          No classified replies yet. {data.unclassified > 0 && `${data.unclassified} pending — classifier runs every 2 minutes.`}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Donut chart */}
          <div>
            <div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 8 }}>Distribution</div>
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, pct }) => `${pct}%`}
                    labelLine={false}
                    style={{ fontSize: 11 }}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ fontSize: 12, border: `1px solid ${BORDER}`, borderRadius: 6 }}
                    formatter={(v, k, p) => [`${v} (${p.payload.pct}%)`, p.payload.name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Breakdown table */}
          <div>
            <div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 8 }}>Breakdown</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ borderBottom: `1px solid ${BORDER}` }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Intent</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Count</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>%</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, width: '40%' }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((r, i) => {
                  const meta = INTENT_LABEL_META[r.label] || { display: r.label, colour: '#94A3B8' };
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #F5F2EE' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: meta.colour }} />
                          {meta.display}
                        </span>
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500 }}>{fmtNumber(r.count)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.pct}%</td>
                      <td style={{ padding: '6px 8px' }}>
                        <div style={{ height: 8, background: '#F1F5F9', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${r.pct}%`, background: meta.colour, transition: 'width 200ms' }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

